/**
 * Network-level input views fronting `NetworkInputs_Agent`.
 *
 * The Agent source carries NO column prefix — every measure is already its bare
 * canonical name (`separatorOilRate`, `jointMaxOilRate`, …) — so this is a pure
 * concern-grouping projection: each shape is authored with `suffix ===
 * canonical` and applied with the default empty prefix, emitting only the
 * `(columns …)` projection and no `(rename …)` ops. The 36 measure timeseries
 * split into three NETWORK-grain surfaces (constraints, economics, capacity),
 * each keeping identity + the `networkName` direct-relation so the agent can
 * scope by / join the network model entity.
 *
 * These are `createReshapedView` (rewrite) views — pure de-prefix/projection
 * over the source schema, owning no fetch — so they need no `deps`; they are a
 * module-level constant spread into `createAppVirtualViews`.
 */

import { createReshapedView, type VirtualView } from '../createReshapedView.ts';
import {
  FLOWLINE_CONSTRAINTS,
  IDENTITY,
  JOINT_CONSTRAINTS,
  NETWORK_CAPACITY,
  NETWORK_ECONOMICS,
  NETWORK_LIMITS,
  NETWORK_RELATION,
  SEPARATOR_CONDITIONS,
  type Shape,
} from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { NetworkInputsAgentView } from '#schema/generated/ipso/views.ts';

/** Identity + the network direct-relation kept unrenamed on every NetworkInputs view. */
const NETWORK_KEEP: Shape = [...IDENTITY, ...NETWORK_RELATION];

/** One concern-scoped NetworkInputs surface: a canonical name, agent doc, and the groups to project. */
type NetworkInputsSpec = ViewFamilySpec<readonly Shape[]> & { readonly groups: readonly Shape[] };

/**
 * The three NetworkInputs subviews, declared as data. Each de-prefixes a
 * concern slice of the source's 36 measures over the NETWORK grain
 * (`networkName`):
 *   - NetworkConstraints: separator conditions + joint constraints + flowline
 *     constraints + the bare manifold/well/pump operating limits.
 *   - NetworkEconomicsView: commodity prices, per-phase processing/disposal/lift
 *     costs, gas barrel-of-oil-equivalent.
 *   - NetworkCapacityView: per-phase processing capacities, available gas-lift
 *     rate, available power.
 */
const NETWORK_INPUTS_SPECS: readonly NetworkInputsSpec[] = [
  {
    name: 'NetworkConstraints',
    description:
      'Network-level operating constraints and input assumptions (NETWORK grain): separator operating + max conditions, joint commingling constraints, flowline limits, and manifold/well/pump operating limits. Filter by networkName (or join the network entity) to scope to a network.',
    groups: [SEPARATOR_CONDITIONS, JOINT_CONSTRAINTS, FLOWLINE_CONSTRAINTS, NETWORK_LIMITS],
  },
  {
    name: 'NetworkEconomicsView',
    description:
      'Network-level economic inputs (NETWORK grain): oil/gas prices, per-phase processing, water-disposal and artificial-lift costs, and the gas barrel-of-oil-equivalent conversion. Filter by networkName (or join the network entity) to scope to a network.',
    groups: [NETWORK_ECONOMICS],
  },
  {
    name: 'NetworkCapacityView',
    description:
      'Network-level capacity inputs (NETWORK grain): per-phase processing-rate capacities plus the available gas-lift rate and power headroom. Filter by networkName (or join the network entity) to scope to a network.',
    groups: [NETWORK_CAPACITY],
  },
];

/**
 * Build the three de-prefixed NetworkInputs surfaces fronting
 * `NetworkInputs_Agent`. Reshape views — no `deps`; a hook because a reshape
 * self-mints its per-row ts-ref cells (needs the client + timeseries minter).
 */
export const useNetworkInputsViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return NETWORK_INPUTS_SPECS.map((spec: NetworkInputsSpec) =>
    createReshapedView({
      name: spec.name,
      description: spec.description,
      source: NetworkInputsAgentView,
      keep: NETWORK_KEEP,
      groups: spec.groups.map((shape: Shape) => ({ shape })),
      client,
      mintSeries,
    }),
  );
};
